import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { Row, Col, Space, Typography, Form, Statistic } from "antd";
import { FormInput, FormButton } from "components/FormItems/FlatFormItems";

import "./GiftcardsAndPromotions.scss";
import GiftCardModal from "./GiftCardModal";
import { useDispatch, useSelector } from "react-redux";
import AddPaymentMethod from "components/AddPaymentMethod/AddPaymentMethod";
import {
  getPurchasablePromoGCs,
  redeemGiftCard,
} from "services/booking.service";
import { getAllPaymentMethods } from "services/paymentMethod.service";
import { getPurchaseableRewards } from "services/rewards.service";
import GiftCardsList from "./GiftCardsList";
import { getCustomerWallet, getCustomerWallets } from "services/auth.services";
import { GIFTCARD_CONFIG_KEY } from "utils/constants";
import Modal from "components/Modal/Modal";
import { useLocation } from "react-router";
import queryString from "query-string";
import Spinner from "components/Spinner/Spinner";
import HorizontalSlide from "components/HorizontalSlide/HorizontalSlide";
import PromoGiftCard from "containers/GiftcardAnonymous/components/PromoGiftcard";

const { Title, Text } = Typography;

const DUMMY_PROMOTIONS = [];

function GiftcardAndPromotions(props) {
  const [form] = Form.useForm();
  const [showSendGiftCardModal, setShowSendGiftCardModal] = useState(false);
  const location = useLocation();
  const qs = queryString.parse(location.search);

  const {
    paymentMethod: { paymentMethods, wallets },
    credit: { balance, isLoading: isCreditLoading },
    customerWallets: {
      data: customerWallets,
      isLoading: isCustomerWalletsLoading,
    },
  } = useSelector((state) => ({
    paymentMethod: state.paymentMethod,
    credit: state.paymentMethod.credit,
    customerWallets: state.paymentMethod.customerWallets,
  }));
  const primaryPaymentMethod =
    paymentMethods?.find?.((card) => card?.isPrimary === true) ??
    paymentMethods?.[0];
  const wallet = wallets?.[0];

  const [showAddCard, setShowAddCard] = useState(false);
  const [showViewAll, setShowViewAll] = useState(false);
  const [showTCs, setShowTCs] = useState(false);

  const [promoGCs, setPromoGCs] = useState({
    data: null,
    isLoading: true,
  });

  const dispatch = useDispatch();

  const { miscSettings, miscLoading } = useSelector((state) => ({
    miscSettings: state.misc.miscSettings,
    miscLoading: state.misc.isLoading,
  }));

  const design = useMemo(() => {
    try {
      const giftCardConfig = miscSettings?.find(
        (setting) => setting.key === GIFTCARD_CONFIG_KEY
      );
      if (giftCardConfig)
        return JSON.parse(decodeURI(giftCardConfig?.value || "{}"));
    } catch (e) {
      console.log(e);
      return;
    }
  }, [miscSettings, miscLoading]);

  const sendCardEvent = (paymentAdded) => {
    if (paymentMethods?.length < 1 && !paymentAdded) {
      setShowAddCard(true);
      return;
    }
    setShowSendGiftCardModal(true);
  };

  useEffect(() => {
    dispatch(getPurchasablePromoGCs())
      .then((res) => {
        if (res.status === "success") {
          setPromoGCs({
            data: res.data,
            isLoading: false,
          });
        } else {
          setPromoGCs({
            data: null,
            isLoading: false,
          });
        }
      })
      .catch(() => {
        setPromoGCs({
          data: null,
          isLoading: false,
        });
      });
  }, []);

  useEffect(() => {
    // dispatch(getCustomerWallet());
    dispatch(getCustomerWallets());
  }, []);

  useEffect(() => {
    if (qs.id) {
      setShowViewAll(true);
    }
  }, []);

  return (
    <div>
      <Row gutter={[20, 16]}>
        <Col
          xs={{
            span: 24,
            order: 1,
          }}
          md={{ span: 16, order: 0 }}
          className="giftcardsAndPromotionsLeftSideWrapper"
        >
          {promoGCs.isLoading ? (
            <Spinner />
          ) : (
            <>
              {promoGCs.data?.length > 0 && (
                <HorizontalSlide
                  className="promoGiftCardSliders max-w-auto"
                  title="Promo Gift Cards"
                >
                  {promoGCs.data.map((item, index) => {
                    return (
                      <PromoGiftCard
                        name={item.programName}
                        description={item.notes}
                        price={item.sellingPrice}
                        // isSelected={item.productId === promotionalProductId}
                        onClick={() => {
                          setShowSendGiftCardModal({
                            promoProduct: item,
                          });
                        }}
                      />
                    );
                  })}
                </HorizontalSlide>
              )}
            </>
          )}

          <Title level={4} className="tradeGothic">
            Gift Cards
          </Title>
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => {
              dispatch(redeemGiftCard(values)).then((res) => {
                if (res?.status === "success") {
                  form.resetFields();
                  // dispatch(getCustomerWallet());
                  dispatch(getCustomerWallets());
                }
              });
            }}
            onFinishFailed={({ values, errorFields, outOfDate }) => {
              form.scrollToField(errorFields[0].name, {
                scrollMode: "if-needed",
                block: "center",
                behavior: "smooth",
              });
            }}
          >
            <FormInput
              variant="underlined"
              name={"redeemCode"}
              label="Enter Gift Card Code"
              placeholder="Eg: 3453 3456 2345 6235"
              styles={{ width: "100%" }}
              rules={[
                {
                  required: true,
                  message: "Enter a valid gift card code",
                },
              ]}
            />
            <div
              className="gcAndPromoControls"
              style={{
                display: "flex",
                gap: 8,
              }}
            >
              <FormButton
                type="submit"
                text="Apply to Wallet"
                className="tradeGothicBold"
                rounded={true}
              />
              <FormButton
                text="View Gift Cards"
                className="tradeGothicBold"
                rounded={true}
                onClick={() => setShowViewAll(true)}
              />
            </div>
          </Form>

          <Title level={4} className="tradeGothic" style={{ marginBottom: 16 }}>
            Or gift your friends the goodness of wellness!
          </Title>
          <FormButton
            onClick={() => sendCardEvent()}
            text="Send a Gift Card"
            className="tradeGothicBold"
            rounded={true}
            formItemStyles={{ marginBottom: 16 }}
          />
          <Typography.Link
            underline
            disabled={!design?.notes}
            onClick={() => setShowTCs(true)}
          >
            Gift Card Terms & Conditions
          </Typography.Link>
          {/* <Title
            level={4}
            className="tradeGothic"
            style={{ marginBottom: 8, paddingTop: 4 }}
          >
            Promitions
          </Title>

          <Text style={{ fontSize: 14 }}>You have no active promotions</Text> */}
        </Col>
        <Col
          xs={24}
          md={8}
          className="flex column  alignStart gap-16"
        >
          <Statistic
            loading={isCreditLoading}
            className="creditsWidget"
            title="Available in wallet"
            value={balance ?? 0}
            precision={2}
            prefix="$"
          />
          <Statistic
            loading={isCustomerWalletsLoading}
            className="creditsWidget"
            title="Available in promotional wallets"
            value={
              customerWallets
                ?.filter((it) => it.isGeneral !== true)
                ?.reduce((acc, iter) => acc + iter.balance, 0) ?? 0
            }
            precision={2}
            prefix="$"
          />
        </Col>
      </Row>
      <GiftCardModal
        primaryPaymentMethod={primaryPaymentMethod}
        visible={showSendGiftCardModal}
        onCancel={() => setShowSendGiftCardModal(false)}
        onSave={() => setShowSendGiftCardModal(false)}
        promoProduct={showSendGiftCardModal?.promoProduct}
      />
      <AddPaymentMethod
        message="Payment method is mandatory for purchasing a gift card."
        saveText={"Save and Order"}
        closable={false}
        forcePrimary={true}
        visible={showAddCard}
        handleSave={(data = []) => {
          const primaryPaymentMethod =
            data.find((card) => card?.isPrimary === true) ?? data?.[0];
          setShowAddCard(false);
          sendCardEvent(primaryPaymentMethod?.paymentMethodId);
        }}
        variant="modal"

        // onCancel={() => setShowAddCard(false)}
      />
      <GiftCardsList
        // onView={(giftCard) => setShowAdd(giftCard)}
        visible={showViewAll}
        onCancel={() => {
          setShowViewAll(false);
        }}
        // loading={loading}
        // giftCards={giftCards}
        // refreshGiftCards={refreshGiftCards}
      />
      <Modal
        title="Gift Card Terms & Conditions"
        visible={showTCs}
        onCancel={() => setShowTCs(false)}
        footer={null}
      >
        <Typography.Text level={4} className="tradeGothic whiteSpacePreWrap">
          {design?.notes}
        </Typography.Text>
      </Modal>
    </div>
  );
}

GiftcardAndPromotions.propTypes = {};

export default GiftcardAndPromotions;
